/**
 * useCouponField — hook générique du champ coupon (remise AFFICHÉE).
 *
 * §74 — 6e surface coupon (panier) : on N'inline PAS une 6e copie. Ce hook compose
 *        les deux briques DRY existantes, sans les réimplémenter :
 *          - décision pure  : `computeCouponState` (src/hooks/couponState.js)
 *          - appel réseau   : `CouponService.fetchCoupon` (src/services/CouponService.js)
 *        Il reproduit le pattern inline d'UECheckoutModal (debounce ~1 s, état appliqué/
 *        expiré/inconnu, `.catch` réseau préservé), paramétré par `amount` et `resetSentinel`.
 *
 * ⚠️ Coupon = remise AFFICHÉE uniquement. Le prix réel est recalculé SERVER-SIDE
 *    (`_calculateCartTotal` honore `couponId`). `discountedTotal` est display-only :
 *    NE JAMAIS s'en servir pour facturer (§46 — argent sécurisé côté serveur).
 *
 * Famille A (resetSentinel='base', défaut) : sur reset/overdue/invalid, le prix reste = `amount`.
 * Famille B (resetSentinel='null')         : sur reset/overdue/invalid, le prix = null.
 *
 * @param {Object}  opts
 * @param {number}  opts.amount          montant de base (number) pour le calcul de remise
 * @param {'base'|'null'} [opts.resetSentinel='base']
 * @param {number}  [opts.debounceMs=1000]
 * @returns {{
 *   couponId: string,              identifiant du coupon résolu ('' si aucun)
 *   couponState: Object,           dernier état brut de computeCouponState
 *   discountedTotal: (number|null) total remisé AFFICHÉ (= amount si pas de coupon en Famille A)
 *   discountPct: number,           pourcentage de remise du coupon résolu (0 si aucun) — pour recalcul AFFICHÉ par-item
 *   onCouponChange: function,      handler onChange du <input> (lit e.target.value, debounce)
 *   fetching: boolean,             requête coupon en vol
 *   error: boolean,                coupon invalide OU expiré OU échec réseau
 *   overdue: boolean,              coupon expiré
 *   applied: boolean,              coupon valide appliqué
 * }}
 */
import { useState, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import * as CouponService from '../services/CouponService';
import { computeCouponState } from './couponState';

export function useCouponField({ amount, resetSentinel = 'base', debounceMs = 1000 } = {}) {
  const [couponState, setCouponState] = useState(() =>
    computeCouponState({ res: '__empty__', amount, resetSentinel })
  );
  const [fetching, setFetching] = useState(false);

  // fetchCoupon — même séquence qu'UECheckoutModal.fetchCoupon (§69, 1:1) :
  //   champ vide → reset sans appel réseau ; sinon CouponService.fetchCoupon → computeCouponState.
  //   `.catch` réseau préservé (error=true, id reset, ne touche pas overdue).
  const fetchCoupon = useCallback((value) => {
    setFetching(true);

    if (!value) {
      setCouponState(computeCouponState({ res: '__empty__', amount, resetSentinel }));
      setFetching(false);
      return;
    }

    CouponService.fetchCoupon(value)
      .then((res) => {
        setCouponState(computeCouponState({ res, amount, resetSentinel }));
        setFetching(false);
      })
      .catch(() => {
        // Échec réseau (préservé 1:1 §69) : error=true, id='', set=false, price=resetSentinel.
        // N'altère PAS overdue (cf. couponState.char.test §71 Famille B).
        setCouponState((prev) => ({
          ...prev,
          error: true,
          set: false,
          id: '',
          price: resetSentinel === 'null' ? null : amount,
        }));
        setFetching(false);
      });
  }, [amount, resetSentinel]);

  // Debounce ~1 s pour éviter un appel par keystroke (recréé si fetchCoupon change).
  const debouncedFetchCoupon = useMemo(
    () => debounce(fetchCoupon, debounceMs),
    [fetchCoupon, debounceMs]
  );

  const onCouponChange = useCallback((e) => {
    const value = (e?.target?.value || '').trim();
    debouncedFetchCoupon(value);
  }, [debouncedFetchCoupon]);

  return {
    couponId: couponState.set ? couponState.id : '',
    couponState,
    discountedTotal: couponState.price,
    discountPct: couponState.set ? (couponState.discount || 0) : 0,
    onCouponChange,
    fetching,
    error: couponState.error,
    overdue: couponState.overdue,
    applied: couponState.set,
  };
}

export default useCouponField;
